/* eslint-disable max-lines -- one orchestrator for every project list read and write; splitting it
   would scatter the shared search-generation guard across files. */
import { useProjectStore } from './project.store';
import { ProjectPersistence as SharedProjectPersistence } from '@/core/shared/project/project.persistence';
import { ProjectFilterTab } from '@/core/shared/project/project.type';
import { useImmediateOutreachStore } from '@/core/jobs/immediate-outreach/immediate-outreach.store';

import MeService from '@/core/shared/me/me.service';
import type {
  Project,
  ProjectSource,
  ProjectWithRelations,
  SearchProjects,
} from '@factoryfixinc/ats-interfaces';
import { JobApplicantStatus, ProjectStatus } from '@factoryfixinc/ats-interfaces';
import type {
  CreateProjectInput,
  DuplicateProjectInput,
  UpdateProjectInput,
} from '@/core/shared/project/project.type';
import { isEqual, omit, unique } from 'radash';
import type { LocationQueryRaw } from 'vue-router';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import { PageSource } from '@/core/shared/tracking/tracking.type';
import SubscriptionService from '../subscription/subscription.service';
import { InternalError } from '../errors/internal.error';
import { ProjectNotFoundError } from '../errors/project-not-found.error';
import { TrackingCopilotSource } from '@/core/shared/tracking/tracking-actions';
import {
  LEGACY_PROJECT_FILTER_QUERY_KEYS,
  PROJECT_FILTER_QUERY_KEYS,
} from '@/core/shared/project/project-filter-query.const';

const READ_PROJECT_FILTER_QUERY_KEYS = [
  ...PROJECT_FILTER_QUERY_KEYS,
  ...LEGACY_PROJECT_FILTER_QUERY_KEYS,
] as const;

type ProjectFilterQuery = Partial<
  Record<(typeof READ_PROJECT_FILTER_QUERY_KEYS)[number] | 'tab', unknown>
>;

const PROJECT_FILTER_TABS = Object.values(ProjectFilterTab) as string[];

/**
 * Reads the jobs scope from `jobsTab`, falling back to a legacy `tab` only when
 * its value is a jobs tab: `tab` is the candidate-pane deep-link key, so
 * `tab=conversation` carries no jobs scope at all.
 */
function readJobsTabQuery(source: ProjectFilterQuery): ProjectFilterTab | undefined {
  for (const raw of [source.jobsTab, source.tab]) {
    if (typeof raw === 'string' && PROJECT_FILTER_TABS.includes(raw)) {
      return raw as ProjectFilterTab;
    }
  }
  return undefined;
}

type InitialSearchEntry = {
  employerId?: number;
  promise: Promise<void>;
  settled: boolean;
  reapply?: Promise<void> | null;
};

function hasFilterQuery(query: ProjectFilterQuery): boolean {
  return (
    READ_PROJECT_FILTER_QUERY_KEYS.some((key) => query[key] !== undefined) ||
    readJobsTabQuery(query) !== undefined
  );
}

export default class ProjectService {
  private static initialSearch: InitialSearchEntry | null = null;
  private store = useProjectStore();
  private immediateOutreachStore = useImmediateOutreachStore();
  private subscriptionService = new SubscriptionService();
  private sharedPersistence = new SharedProjectPersistence();
  private meService = new MeService();

  public get projects(): ProjectWithRelations<'candidates'>[] {
    return this.store.projects;
  }

  public set projects(projects: ProjectWithRelations<'candidates'>[]) {
    this.store.projects = projects;
  }

  public get currentProject(): ProjectWithRelations<'candidates'> | undefined {
    return this.store.currentProject;
  }

  public get employerLocations(): string[] | undefined {
    return this.store.employerLocations;
  }

  public get title(): string {
    return this.store.title;
  }

  public set title(title: string) {
    this.store.title = title;
  }

  public get titleOrReqId(): string {
    return this.store.titleOrReqId;
  }

  public set titleOrReqId(titleOrReqId: string) {
    this.store.titleOrReqId = titleOrReqId;
  }

  public get source(): ProjectSource | null {
    return this.store.source;
  }

  public set source(source: ProjectSource | null) {
    this.store.source = source;
  }

  public get copilot(): boolean | null {
    return this.store.copilot;
  }

  public set copilot(copilot: boolean | null) {
    this.store.copilot = copilot;
  }

  public get status(): ProjectStatus | null {
    return this.store.status;
  }

  public set status(status: ProjectStatus | null) {
    this.store.status = status;
  }

  public get locations(): string[] {
    return this.store.locations;
  }

  public set locations(location: string[]) {
    this.store.locations = location;
  }

  public get teamIds(): number[] {
    return this.store.teamIds;
  }

  public set teamIds(teamIds: number[]) {
    this.store.teamIds = teamIds;
  }

  public get isSearchingProjects(): boolean {
    return this.store.isSearchingProjects;
  }

  public get isSearchingMoreProjects(): boolean {
    return this.store.isSearchingMoreProjects;
  }

  public get didSelectUserInProjectFilters(): boolean {
    return this.store.didSelectUserInProjectFilters;
  }

  public set didSelectUserInProjectFilters(value: boolean) {
    this.store.didSelectUserInProjectFilters = value;
  }

  public get hasCopilotFilter(): boolean {
    return this.store.copilot === true;
  }

  public get hasLocationFilter(): boolean {
    return this.store.locations.length > 0;
  }

  public get hasTeamFilter(): boolean {
    return this.store.teamIds.length > 0;
  }

  public get hasSourceFilter(): boolean {
    return Boolean(this.store.source) && this.store.source !== 'all';
  }

  public get hasStatusFilter(): boolean {
    return this.store.status !== 'live';
  }

  public get filtersCount(): number {
    // Check if we're in My Jobs tab and only the current user is selected
    const isMyJobsTabSelected = this.selectedTab === ProjectFilterTab.MY_JOBS;
    const currentUserId = this.meService.userProfile?.id;
    const hasSingleTeamMember = this.teamIds?.length === 1;
    const isCurrentUserSelected = hasSingleTeamMember && this.teamIds[0] === currentUserId;
    // Don't count team filter if My Jobs tab is selected and only the current user is in the team filter
    const shouldCountTeamFilter = !(isMyJobsTabSelected && isCurrentUserSelected);
    const teamFilterCount = shouldCountTeamFilter ? Number(this.hasTeamFilter) : 0;
    return (
      Number(this.hasLocationFilter) +
      teamFilterCount +
      Number(this.hasSourceFilter) +
      Number(this.hasStatusFilter)
    );
  }

  public get selectedTab(): ProjectFilterTab {
    return this.store.selectedTab;
  }

  public set selectedTab(tab: ProjectFilterTab) {
    this.store.selectedTab = tab;
  }

  public get didLoadProjects(): boolean {
    return this.store.didLoadProjects;
  }

  public set didLoadProjects(value: boolean) {
    this.store.didLoadProjects = value;
  }

  public set isSearchingProjects(value: boolean) {
    this.store.isSearchingProjects = value;
  }

  public get areNoProjectsAvailable(): boolean {
    return (
      this.didLoadProjects &&
      !this.store.hasProjects &&
      !this.isSearchingProjects &&
      !this.isSearchingMoreProjects
    );
  }

  public get projectTitlesByJobId(): Map<number, string> {
    return this.store.projectTitlesByJobId;
  }

  public get lastSelectedSourcingProjectId(): number | undefined {
    return this.store.lastSelectedSourcingProjectId;
  }

  public set lastSelectedSourcingProjectId(lastSelectedSourcingProjectId: number | undefined) {
    this.store.lastSelectedSourcingProjectId = lastSelectedSourcingProjectId;
  }

  public get isProjectFromDeepLinkRemoved(): boolean {
    return this.store.isProjectFromDeepLinkRemoved;
  }

  public set isProjectFromDeepLinkRemoved(value: boolean) {
    this.store.isProjectFromDeepLinkRemoved = value;
  }

  /**
   * Updates the selected tab based on team filter selection.
   * If only the current user is selected in the team filter, switches to My Jobs tab.
   * If no team members are selected, switches to All Jobs tab.
   */
  public updateSelectedTabBasedOnTeamFilter(): void {
    const currentUserId = this.meService.userProfile?.id;
    const hasSingleTeamMember = this.teamIds.length === 1;
    const isCurrentUserSelected = hasSingleTeamMember && currentUserId === this.teamIds[0];
    if (isCurrentUserSelected) {
      // Switch to My Jobs tab when only the current user is selected
      this.selectedTab = ProjectFilterTab.MY_JOBS;
    } else {
      // If no team members are selected, switch to All Jobs tab
      this.selectedTab = ProjectFilterTab.ALL_JOBS;
    }
  }

  private getSearchParams(): Partial<SearchProjects> {
    // Search Params
    const searchParams: Partial<SearchProjects> = {
      includeTotalCandidates: true,
      // Cloned: `store.pagination` is reactive, so keeping the reference would let a
      // later page change silently rewrite the record of what was already sent.
      pagination: { ...this.store.pagination },
    };

    if (this.store.copilot === true) {
      searchParams.copilot = this.store.copilot;
    }

    if (this.store.locations.length > 0) {
      searchParams.locations = this.store.locations;
    }

    if (this.store.title) {
      searchParams.title = this.store.title;
    }

    if (this.store.titleOrReqId) {
      searchParams.titleOrReqId = this.store.titleOrReqId;
    }

    if (this.store.status) {
      searchParams.status = this.store.status;
    }

    if (this.store.teamIds.length) {
      searchParams.teamIds = this.store.teamIds;
    }

    if (this.store.source) {
      searchParams.source = this.store.source;
    }

    return searchParams;
  }

  public async searchProjects(
    opts: {
      skipOnSameSearch?: boolean;
      skipTracking?: boolean;
      pageSource?: PageSource;
      shouldCommitResults?: () => boolean;
    } = {
      skipOnSameSearch: false,
      skipTracking: false,
    },
  ): Promise<void> {
    // Skip if search params are the same unless skipOnSameSearch is false
    // The page is left out: load-more advances it without changing the filter.
    if (
      opts.skipOnSameSearch &&
      isEqual(
        omit(this.store.searchParams, ['pagination']),
        omit(this.getSearchParams(), ['pagination']),
      )
    ) {
      return;
    }

    const employerId = this.meService.employer?.id as number;
    const requestId = ++this.store.projectListRequestId;
    const isCurrent = () =>
      this.store.projectListRequestId === requestId &&
      this.meService.employer?.id === employerId &&
      (opts.shouldCommitResults?.() ?? true);

    try {
      this.store.isSearchingProjects = true;

      // Reset pagination
      this.store.pagination.page = 1;

      this.store.searchParams = this.getSearchParams();

      const result = await this.sharedPersistence.searchProjectListPage({
        employerId,
        search: this.store.searchParams,
      });

      if (!isCurrent()) return;

      this.store.projects = result.projects;
      this.fillProjectTitlesByJobId(this.store.projects);

      if (!opts?.skipTracking) {
        if (opts.pageSource === PageSource.Sourcing) {
          TrackingService.trackAction(TrackingActionName.SOURCING_PAGE_JOBS_FILTERED_OR_SEARCHED, {
            filters: {
              textual_search: this.title,
              copilot_status: this.copilot,
              location: this.locations,
              status: this.status,
              teamIds: this.teamIds,
            },
            source: 'Sourcing',
          });
        }

        if (opts.pageSource === PageSource.Conversations) {
          TrackingService.trackAction(
            TrackingActionName.CONVERSATIONS_PAGE_JOBS_FILTERED_OR_SEARCHED,
            {
              filters: {
                locations: this.locations,
                team: this.teamIds,
                hide_archived: this.status === 'live',
                textual_search: this.title,
              },
            },
          );
        }
      }
      // Moving this to the end of the `try` block to avoid a small visual bug
      // where the skeleton loader hides before rendering the projects
      // (vs using the `finally` block)
      const myTimeout = setTimeout(() => {
        clearTimeout(myTimeout);
        if (!isCurrent()) return;
        this.store.isSearchingProjects = false;
      }, 150);
    } catch (error) {
      // A superseded failure must not clear the live flight's skeleton or reach
      // the caller's catch, where it would raise a snackbar for a discarded list.
      if (!isCurrent()) return;
      this.store.isSearchingProjects = false;
      throw error;
    }
  }

  public async searchMoreProjects(): Promise<void> {
    // A page can only extend a landed page 1; until then it would be appended to the old list.
    if (this.store.isSearchingProjects) return;

    const employerId = this.meService.employer?.id as number;
    // Captured, not minted: a page belongs to the list generation it extends.
    const requestId = this.store.projectListRequestId;
    const isCurrent = () =>
      this.store.projectListRequestId === requestId && this.meService.employer?.id === employerId;
    const previousPage = this.store.pagination.page;

    try {
      this.store.isSearchingMoreProjects = true;

      // Increment page
      this.store.pagination.page = previousPage + 1;

      const searchParams = this.getSearchParams();

      const result = await this.sharedPersistence.searchProjectListPage({
        employerId,
        search: searchParams,
      });

      if (!isCurrent()) return;

      this.store.projects = [...this.store.projects, ...result.projects];
      this.fillProjectTitlesByJobId(this.store.projects);
    } catch (error) {
      if (!isCurrent()) return;
      this.store.pagination.page = previousPage;
      throw error;
    } finally {
      this.store.isSearchingMoreProjects = false;
    }
  }

  /**
   * Resolves the effective jobs filter and runs the first list search for it,
   * once per employer, handing the same promise to every later caller that
   * brings no explicit URL filter of its own. Owning
   * both here is what keeps the landing route and the sidebar from issuing
   * competing searches, and it is the only path that works on mobile, where no
   * sidebar component ever mounts.
   */
  public async ensureInitialSearch(
    opts: {
      query?: ProjectFilterQuery;
      onFilterResolved?: (query: LocationQueryRaw) => unknown;
      pageSource?: PageSource;
    } = {},
  ): Promise<void> {
    const employerId = this.meService.employer?.id;
    const memoized = ProjectService.initialSearch;
    if (memoized && memoized.employerId === employerId) {
      if (memoized.reapply) return memoized.reapply;
      // A remount of the jobs layout lands here too, and its URL must still win.
      if (memoized.settled && opts.query && hasFilterQuery(opts.query)) {
        const reapply = this.reapplyFilterQuery(opts.query, opts.pageSource).finally(() => {
          if (memoized.reapply === reapply) memoized.reapply = null;
        });
        memoized.reapply = reapply;
        return reapply;
      }
      return memoized.promise;
    }

    // Only a successful bootstrap stays memoized, and only this entry may drop
    // itself: a newer employer's bootstrap must survive an older one failing.
    const entry: InitialSearchEntry = { employerId, promise: Promise.resolve(), settled: false };
    entry.promise = this.runInitialSearch({
      ...opts,
      onFailed: () => {
        if (ProjectService.initialSearch === entry) ProjectService.initialSearch = null;
      },
    }).finally(() => {
      entry.settled = true;
    });
    ProjectService.initialSearch = entry;
    return entry.promise;
  }

  private async reapplyFilterQuery(
    query: ProjectFilterQuery,
    pageSource?: PageSource,
  ): Promise<void> {
    const previousQuery = this.toFilterQuery();
    this.applyFilterQuery(query);
    if (isEqual(previousQuery, this.toFilterQuery())) return;

    this.saveFilterQuery();
    try {
      await this.searchProjects({ skipTracking: true, pageSource });
    } catch (error) {
      ErrorService.captureException(error);
      SnackbarService.critical('Failed to load jobs');
    }
  }

  /** Drops the memoized first search. Tests and a signed-out session need this. */
  public static resetInitialSearch(): void {
    ProjectService.initialSearch = null;
  }

  private async runInitialSearch(opts: {
    query?: ProjectFilterQuery;
    onFilterResolved?: (query: LocationQueryRaw) => unknown;
    onFailed?: () => void;
    pageSource?: PageSource;
  }): Promise<void> {
    const urlQuery = opts.query ?? {};
    const hasUrlFilter = hasFilterQuery(urlQuery);

    this.applyFilterQuery(hasUrlFilter ? urlQuery : this.readSavedFilterQuery());
    this.saveFilterQuery();

    try {
      await this.searchProjects({ skipTracking: true, pageSource: opts.pageSource });
    } catch (error) {
      // Resolve either way: a caller awaiting the landing must never hang on a
      // failed bootstrap.
      ErrorService.captureException(error);
      SnackbarService.critical('Failed to load jobs');
      opts.onFailed?.();
    }

    await this.settleProjectAvailability();

    this.didLoadProjects = true;

    if (!hasUrlFilter) {
      await opts.onFilterResolved?.(this.toFilterQuery());
    }
  }

  /**
   * Settles whether the employer has any jobs at all, keeping "no jobs match
   * this filter" apart from "this employer has no jobs". A list with rows
   * answers it for free; only an empty filtered result costs a second request.
   */
  private async settleProjectAvailability(): Promise<void> {
    if (this.projects.length) {
      this.store.hasProjects = true;
      return;
    }

    if (!this.hasEffectiveFilter) {
      this.store.hasProjects = false;
      return;
    }

    try {
      await this.searchAvailableProjects();
    } catch (error) {
      ErrorService.captureException(error);
    }
  }

  /** Whether the search that just ran narrowed the list relative to no filter at all. */
  private get hasEffectiveFilter(): boolean {
    const { copilot, locations, title, titleOrReqId, status, teamIds } = this.getSearchParams();
    return Boolean(
      copilot ||
        locations?.length ||
        title ||
        titleOrReqId ||
        status ||
        teamIds?.length ||
        this.hasSourceFilter,
    );
  }

  /**
   * Applies a filter expressed as a URL query or a saved filter. Precedence is
   * the caller's: an explicit URL query, else the saved filter, else defaults.
   */
  public applyFilterQuery(query?: ProjectFilterQuery | null): void {
    const source = query ?? {};

    if (source.titleOrReqId) {
      this.titleOrReqId = String(source.titleOrReqId);
    }

    if (source.copilot) {
      this.copilot = String(source.copilot) === 'true' ? true : null;
    }

    if (source.source) {
      this.source = source.source as ProjectSource;
    }

    if (source.location) {
      this.locations = Array.isArray(source.location)
        ? (source.location as string[])
        : [String(source.location)];
    }

    if (source.locations) {
      this.locations = Array.isArray(source.locations)
        ? (source.locations as string[])
        : [String(source.locations)];
    }

    if (source.status) {
      this.status = source.status === ProjectStatus.LIVE ? ProjectStatus.LIVE : null;
    }

    this.applyTeamFilterQuery(source);
    this.repairMyJobsTeamFilter();
  }

  private applyTeamFilterQuery(source: ProjectFilterQuery): void {
    // Explicit team ids are the scope, including an empty list: a Filters apply,
    // a saved custom-team filter and a shared team link all carry them, and the
    // tab is derived from them rather than allowed to overrule them.
    if (source.teamIds !== undefined) {
      this.teamIds = Array.isArray(source.teamIds)
        ? (source.teamIds as (string | number)[]).map(Number)
        : [Number(source.teamIds)];
      this.didSelectUserInProjectFilters = true;
      this.updateSelectedTabBasedOnTeamFilter();
      return;
    }

    // Without team ids the tab is authoritative: All jobs is otherwise encoded as
    // their absence, which is indistinguishable from "no filter given", so a
    // shared All-jobs link used to resolve to the recipient's saved filter.
    const jobsTab = readJobsTabQuery(source);

    if (jobsTab === ProjectFilterTab.ALL_JOBS) {
      this.teamIds = [];
      this.didSelectUserInProjectFilters = true;
      this.selectedTab = ProjectFilterTab.ALL_JOBS;
      return;
    }

    if (jobsTab === ProjectFilterTab.MY_JOBS) {
      this.selectOwnUserInProjectFilters({ force: true });
      this.selectedTab = ProjectFilterTab.MY_JOBS;
      return;
    }

    this.selectOwnUserInProjectFilters();
    this.updateSelectedTabBasedOnTeamFilter();
  }

  /**
   * Selects the signed-in user as the only team member, once per session. The
   * latch is set only after the membership check, so a user who is not in the
   * employer team list does not burn it and lose the filter for the session.
   */
  public selectOwnUserInProjectFilters(opts: { force?: boolean } = {}): void {
    if (!opts.force && this.didSelectUserInProjectFilters) {
      return;
    }

    const ownUserProfileId = this.meService.userProfile?.id;
    const isOwnUserProfileInTeamList = this.meService.employerUsers?.some(
      (user) => user.id === ownUserProfileId,
    );

    if (!ownUserProfileId || !isOwnUserProfileInTeamList) {
      return;
    }

    this.didSelectUserInProjectFilters = true;
    this.teamIds = [ownUserProfileId];
    this.selectedTab = ProjectFilterTab.MY_JOBS;
  }

  /** Keeps the My-jobs tab from ever issuing an unfiltered search. */
  public repairMyJobsTeamFilter(): void {
    if (this.selectedTab !== ProjectFilterTab.MY_JOBS || this.teamIds.length) {
      return;
    }

    const currentUserId = this.meService.userProfile?.id;
    if (currentUserId) {
      this.teamIds = [currentUserId];
    }
  }

  // Left out for My jobs: that tab resolves to whoever opens the link, and the sender's
  // id would outrank it.
  public get teamIdsQuery(): string[] | undefined {
    if (this.selectedTab === ProjectFilterTab.MY_JOBS || !this.teamIds.length) return undefined;
    return this.teamIds.map(String);
  }

  /** The current filter as URL query params, for the address bar and sharing. */
  public toFilterQuery(): LocationQueryRaw {
    return {
      jobsTab: this.selectedTab,
      teamIds: this.teamIdsQuery,
      locations: this.locations.length ? this.locations : undefined,
      // Omitting it would read back as the LIVE default, hiding archived jobs.
      status: this.status === ProjectStatus.LIVE ? ProjectStatus.LIVE : 'all',
      source: this.source ?? undefined,
      copilot: this.copilot ? String(this.copilot) : undefined,
      titleOrReqId: this.titleOrReqId || undefined,
    };
  }

  public readSavedFilterQuery(): ProjectFilterQuery | null {
    try {
      const saved = localStorage.getItem(this.filterStorageKey);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      // A corrupt saved filter falls back to the default rather than blocking.
      ErrorService.captureException(error);
      return null;
    }
  }

  public saveFilterQuery(): void {
    const queryToSave = {
      copilot: this.copilot,
      locations: this.locations,
      // Saved as REMOVED because `null` is read back as the LIVE default.
      status: this.status ? this.status : ProjectStatus.REMOVED,
      source: this.source,
      jobsTab: this.selectedTab,
      teamIds: this.teamIds,
    };

    localStorage.setItem(this.filterStorageKey, JSON.stringify(queryToSave));
  }

  private get filterStorageKey(): string {
    return `search-query-${this.meService.userProfile?.id}-${this.meService.employer?.id}`;
  }

  public async getProjectById(
    projectId: number,
    options: { select?: boolean; signal?: AbortSignal } = {},
  ): Promise<Project> {
    const employerId = this.meService.employer?.id as number;
    const project = await this.sharedPersistence.getProjectById(employerId, projectId);

    if (!project) {
      throw new ProjectNotFoundError(projectId, { employerId });
    }

    // Will update the project in the store if it exists
    const index = this.store.projects.findIndex((p) => p.id === project.id);
    if (index !== -1) {
      this.store.projects[index] = project;
    }
    // Background refreshes must not switch back to a project the user has left.
    if (options.select === false && this.store.currentProject?.id !== project.id) return project;
    if (options.signal?.aborted) return project;
    this.store.currentProject = project;
    return project;
  }

  public selectFirstProject(): ProjectWithRelations<'candidates'> | undefined {
    const project = this.store.projects[0];
    if (project) {
      this.store.currentProject = project;
      return project;
    }
  }

  public async getProjectByJobIdFromDeepLink(
    jobId: number,
    options: { signal?: AbortSignal } = {},
  ): Promise<Project> {
    const employerId = this.meService.employer?.id as number;
    let project: ProjectWithRelations<'candidates'> | undefined;

    try {
      project = await this.sharedPersistence.getProjectByJobId(employerId, jobId);

      if (!project) {
        throw new InternalError('Project not found', {
          data: { employerId, jobId },
        });
      }
    } catch (error) {
      if (
        error instanceof InternalError &&
        JSON.parse((error as InternalError).message).message === 'Project is removed'
      ) {
        if (!options.signal?.aborted) this.store.isProjectFromDeepLinkRemoved = true;
        return {} as Project;
      }

      throw error;
    }

    if (options.signal?.aborted) return project;

    // Remove the current project from the store if it exists and add it to the top
    this.store.projects = this.store.projects.filter((p) => p.id !== project?.id);
    this.store.projects.unshift(project);
    this.store.currentProject = project;

    return project;
  }

  public getLocalProjectById(projectId: number): Project | undefined {
    return this.projects.find((project) => project.id === projectId);
  }

  public getLocalProjectByJobId(jobId: number): Project | undefined {
    return this.projects.find((project) => project.jobId === jobId);
  }

  public async searchAvailableProjects(): Promise<void> {
    const result = await this.sharedPersistence.searchProjects({
      employerId: this.meService.employer?.id as number,
      search: {},
    });

    this.store.hasProjects = result.projects.length > 0;
  }

  public async fetchProjectsTitleByJobIds(jobIds: number[]): Promise<void> {
    const existingJobIds = new Set(this.store.projects.map((project) => project.jobId));
    const missingJobIds = unique(jobIds.filter((jobId) => jobId && !existingJobIds.has(jobId)));

    if (missingJobIds.length === 0) {
      return;
    }

    const result = await this.sharedPersistence.searchProjects({
      employerId: this.meService.employer?.id as number,
      search: {
        jobIds: missingJobIds,
      },
    });

    this.fillProjectTitlesByJobId(result.projects);
  }

  private fillProjectTitlesByJobId(projects: ProjectWithRelations<'candidates'>[]): void {
    projects.forEach((project) => {
      this.store.projectTitlesByJobId.set(project.jobId, project.title);
    });
  }

  public async getEmployerLocations(): Promise<void> {
    const result = await this.sharedPersistence.getEmployerLocations({
      employerId: this.meService.employer?.id as number,
    });

    this.store.employerLocations = result.locations;
  }

  public async addWatcher(
    projectId: number,
    userProfileId: number,
    watcher?: NonNullable<Project['watchers']>[number],
  ): Promise<void> {
    const employerId = this.meService.employer?.id as number;
    await this.sharedPersistence.addWatcher(employerId, projectId, userProfileId);

    // If we have the watcher, add it to the project locally
    if (watcher) {
      const project = this.store.projects.find((project) => project.id === projectId);
      if (project) {
        if (Array.isArray(project.watchers)) {
          project.watchers.push(watcher);
        } else {
          project.watchers = [watcher];
        }
      }
    }
  }

  public async removeWatcher(projectId: number, userProfileId: number): Promise<void> {
    const employerId = this.meService.employer?.id as number;
    await this.sharedPersistence.removeWatcher(employerId, projectId, userProfileId);

    // Remove watcher from the project locally
    const project = this.store.projects.find((project) => project.id === projectId);
    if (project && project.watchers) {
      project.watchers = project.watchers.filter((watcher) => watcher.id !== userProfileId);
    }
  }

  public async updateAssignee(
    projectId: number,
    userProfileId: number | null,
    assignee?: Project['assignee'],
  ): Promise<void> {
    const employerId = this.meService.employer?.id as number;
    await this.sharedPersistence.updateAssignee(employerId, projectId, userProfileId);

    // If we have the assignee, or it was cleared, reflect it on the project locally
    if (assignee || userProfileId === null) {
      const project = this.store.projects.find((project) => project.id === projectId);
      if (project) {
        project.assignee = assignee ?? null;
      }
    }
  }

  public async archiveProject(projectId: number, wasCopilotActivated?: boolean): Promise<void> {
    const employerId = this.meService.employer?.id as number;
    await this.sharedPersistence.archiveProject(employerId, projectId);

    if (wasCopilotActivated) {
      this.subscriptionService.updateCopilotsUsedLocalValue(-1);
    }

    // Update project locally
    const project = this.store.projects.find((project) => project.id === projectId);
    if (project) {
      project.copilot = false;
      project.status = ProjectStatus.ARCHIVED;
    }
  }

  public async activateProject(projectId: number): Promise<void> {
    const employerId = this.meService.employer?.id as number;
    await this.sharedPersistence.activateProject(employerId, projectId);

    // Update project locally
    const project = this.projects.find((project) => project.id === projectId);
    if (project) {
      project.status = ProjectStatus.LIVE;
    }
  }

  public async deleteProject(projectId: number): Promise<void> {
    const employerId = this.meService.employer?.id as number;
    await this.sharedPersistence.deleteProject(employerId, projectId);

    // Remove project from the store
    this.store.projects = this.store.projects.filter((project) => project.id !== projectId);
    this.store.currentProject = this.store.projects?.[0] || undefined;
  }

  public async updateProject(projectId: number, data: UpdateProjectInput): Promise<void> {
    const employerId = this.meService.employer?.id as number;
    await this.sharedPersistence.updateProject(employerId, projectId, data);
  }

  public async duplicateProject(projectId: number, data: DuplicateProjectInput): Promise<Project> {
    const employerId = this.meService.employer?.id as number;
    const project = this.sharedPersistence.duplicateProject(employerId, projectId, data);

    return project;
  }

  public async createNewProjectAndJob(): Promise<Project> {
    const projectData: CreateProjectInput = {
      title: 'No title',
      location: this.getCompanyLocation(),
      activeOutreachEnabled: true,
    };

    const project = await this.createProject(projectData);

    TrackingService.trackAction(TrackingActionName.COPILOT_ENABLE_STARTED, {
      source: TrackingCopilotSource.PLUS_NEW_JOB_BUTTON,
      project_id: project.id,
      job_id: project.jobId,
    });

    return project;
  }

  public async createProject(data: CreateProjectInput): Promise<Project> {
    const employerId = this.meService.employer?.id as number;
    const project = await this.sharedPersistence.createProject(employerId, data);

    return project;
  }

  public async updateProjectCopilotStatus(projectId: number, enabled: boolean): Promise<void> {
    const employerId = this.meService.employer?.id as number;
    await this.sharedPersistence.updateCopilotStatus(employerId, projectId, enabled);

    this.subscriptionService.updateCopilotsUsedLocalValue(enabled ? 1 : -1);

    // Update project locally if it exists in the projects array
    const project = this.projects.find((project) => project.id === projectId);
    if (project) {
      project.copilot = enabled;
    }

    // Manage immediate outreach state
    if (enabled) {
      // When enabling copilot, check if project has activeOutreachEnabled
      // If we have the project locally, use it; otherwise assume it's enabled (default for new projects)
      const hasActiveOutreach = project ? project.activeOutreachEnabled : true;
      if (hasActiveOutreach) {
        this.immediateOutreachStore.enabledProjectIds.add(projectId);
      }
    } else {
      // Remove from set when copilot is disabled
      this.immediateOutreachStore.enabledProjectIds.delete(projectId);
    }
  }

  public updateLocalProjectJobApplicationStatusCount({
    jobId,
    newStatus,
    previousStatus,
  }: {
    jobId: number;
    newStatus: JobApplicantStatus;
    previousStatus: JobApplicantStatus;
  }): void {
    // This will avoid double counting when the status is the same
    if (newStatus === previousStatus) return;

    // Update on selected project if it matches the jobId
    const currentProject = this.store.currentProject;
    if (currentProject?.jobId === jobId && currentProject.candidates) {
      // To avoid negative values
      const previousStatusCount = (currentProject.candidates as Record<JobApplicantStatus, number>)[
        previousStatus
      ];
      (currentProject.candidates as Record<JobApplicantStatus, number>)[previousStatus] -=
        previousStatusCount <= 0 ? 0 : 1;
      (currentProject.candidates as Record<JobApplicantStatus, number>)[newStatus] += 1;
    }
  }

  private getCompanyLocation(): string {
    let location = '';
    location += this.meService.employer?.city ? `${this.meService.employer?.city}, ` : '';
    location += this.meService.employer?.state ? `${this.meService.employer?.state}, ` : '';
    location += 'USA';
    return location;
  }
}
